import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useResponsive } from '@parsina/framework';
import PatientDetailWeb from './PatientDetail.web.jsx';
import PatientDetailMobile from './PatientDetail.mobile.jsx';
import { PATIENT_SECTION_IDS, DEFAULT_PATIENT_SECTION } from './PatientDetail.shared.js';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';

// The patient workspace's ROUTING contract, kept out of the surfaces themselves.
//
//  • The section is a URL SEGMENT (`/patients/:id/:tab?`), so refresh, back and
//    a shared link all restore the tab that was being read.
//  • Switching tabs REPLACEs, so the browser's history holds one entry per
//    record rather than one per glance.
//  • 'summary' is the default and is omitted from the URL.
//  • Back goes back when there IS a history entry, and falls to بیماران when
//    the record was opened from a deep link — `navigate(-1)` on a fresh tab
//    leaves the app entirely. The register, not امروز: a patient record was
//    reached from the register in every case the app itself can produce.
//  • The raw `:tab` segment is validated against the section set HERE, so an
//    unknown one lands on خلاصه instead of a blank pane. THE SET COMES FROM
//    PatientDetail.shared.js, which is also what both surfaces render from:
//    'chart' was rendered by the desktop surface while missing from this list,
//    so /patients/<id>/chart silently fell back to خلاصه and the odontogram was
//    unreachable by URL. One list is why that cannot happen again — and it also
//    covers the mobile case DESIGN-CONCEPT-MOBILE §4C names, where a web-only
//    tab id survives a viewport switch.
//  • ≤768px gets a genuinely different surface (§1.1), not this one reflowed.
//    Both are handed the SAME id/tab/setTab/goBack, so the two can differ in
//    every pixel and in nothing else.

export const SECTIONS = PATIENT_SECTION_IDS;
export const DEFAULT_SECTION = DEFAULT_PATIENT_SECTION;

const PatientDetailScreen = () => {
  const { id, tab } = useParams();
  const location = useLocation();
  const rrNavigate = useNavigate();
  const navigate = useAppNavigate();
  const { isMobile } = useResponsive();

  const section = SECTIONS.includes(tab) ? tab : DEFAULT_SECTION;

  const setTab = React.useCallback(
    (next) => {
      const suffix = next && next !== DEFAULT_SECTION ? `/${next}` : '';
      rrNavigate(`/patients/${id}${suffix}${location.search}`, {
        replace: true,
        state: location.state,
      });
    },
    [id, location.search, location.state, rrNavigate],
  );

  const goBack = React.useCallback(() => {
    if (location.key && location.key !== 'default') rrNavigate(-1);
    else navigate('patients');
  }, [location.key, rrNavigate, navigate]);

  const props = { id, tab: section, setTab, goBack };
  return isMobile ? <PatientDetailMobile {...props} /> : <PatientDetailWeb {...props} />;
};

export default PatientDetailScreen;
